import React from 'react';
import { Button, Modal } from '@aibuild/ui';
import type { ProcessDetailDto } from '@api/processes';
import {
  Row,
  RowLabel,
  RowList,
  RowValue,
} from './ProcessDetailsDialog.styled';

export interface ProcessDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  detail: ProcessDetailDto | null;
  dataTestId?: string;
}

const STATUS_LABEL: Record<ProcessDetailDto['status'], string> = {
  draft: 'Draft',
  published: 'Published',
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Locale 'en-GB' renders as `15 Mar 2025, 14:30` — matches the
  // design's `${day} ${month} ${year}, ${hh}:${mm}` shape.
  return new Intl.DateTimeFormat('en-GB', DATE_FORMAT).format(d);
};

interface DetailsRow {
  readonly label: string;
  readonly value: string;
}

const buildRows = (detail: ProcessDetailDto): readonly DetailsRow[] => [
  { label: 'Author', value: detail.createdBy?.name ?? '—' },
  { label: 'Date created', value: formatDate(detail.createdAt) },
  { label: 'Last edit', value: formatDate(detail.updatedAt) },
  { label: 'Status', value: STATUS_LABEL[detail.status] ?? '—' },
];

const ProcessDetailsDialog: React.FC<ProcessDetailsDialogProps> = ({
  open,
  onClose,
  detail,
  dataTestId = 'process-details-dialog',
}) => {
  const rows = detail ? buildRows(detail) : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Process details"
      size="sm"
      dataTestId={dataTestId}
      footer={
        <Button
          variant="filled"
          onClick={onClose}
          dataTestId={`${dataTestId}__footer-close`}
        >
          Close
        </Button>
      }
    >
      <RowList data-testid={`${dataTestId}__list`}>
        {rows.map((row) => (
          <Row key={row.label} data-testid={`${dataTestId}__row-${row.label}`}>
            <RowLabel>{row.label}</RowLabel>
            <RowValue>{row.value}</RowValue>
          </Row>
        ))}
      </RowList>
    </Modal>
  );
};

export default ProcessDetailsDialog;
